import React, { FunctionComponent, useEffect, useState, useRef } from "react"
import { useSelector, useDispatch } from "react-redux"
import DOMPurify from "dompurify"
import { ApplicationState } from "../../core/types"
import { sendInteractionEvent, dismissModal } from "../../data/stores/in-app-comms/in-app-comms-actions"
import { InAppMessageSeverity } from "../../data/model/in-app-comms-model"

const SEVERITY_ICONS: Record<InAppMessageSeverity, string> = {
    critical:      "⛔",
    warning:       "⚠️",
    informational: "ℹ️",
}

export const InAppCommsModal: FunctionComponent = () => {
    const modal = useSelector((s: ApplicationState) => s.inAppComms.modal)
    const dispatch = useDispatch()
    const [ackChecked, setAckChecked] = useState(false)
    const [mediaOrientation, setMediaOrientation] = useState<"portrait" | "landscape" | null>(null)
    const [mediaError, setMediaError] = useState(false)
    const deliveredIdRef = useRef<string | null>(null)

    useEffect(() => {
        setAckChecked(false)
        setMediaOrientation(null)
        setMediaError(false)
        if (modal && modal.inappMessageId !== deliveredIdRef.current) {
            deliveredIdRef.current = modal.inappMessageId
            dispatch(sendInteractionEvent(modal.inappMessageId, "delivered"))
        }
    }, [modal?.inappMessageId])

    if (!modal) return null

    const canDismiss = !modal.requiresAck || ackChecked

    const handleDismiss = () => {
        if (!canDismiss) return
        dispatch(dismissModal(modal.inappMessageId))
        dispatch(sendInteractionEvent(modal.inappMessageId, ackChecked ? "acknowledged" : "dismissed"))
    }

    const handleMediaLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget
        setMediaOrientation(img.naturalWidth > img.naturalHeight ? "landscape" : "portrait")
    }

    const sanitizedBody = DOMPurify.sanitize(modal.body)
    const contentClass = [
        "in-app-comms-modal__content",
        mediaOrientation ? `media-${mediaOrientation}` : "",
    ].filter(Boolean).join(" ")

    return (
        <div className={`in-app-comms-modal-overlay${modal.requiresAck ? " in-app-comms-modal-overlay--blocking" : ""}`}>
            <div className={`in-app-comms-modal in-app-comms-modal--${modal.severity}`}>

                <div className="in-app-comms-modal__header">
                    <span className="in-app-comms-modal__severity-icon">
                        {SEVERITY_ICONS[modal.severity]}
                    </span>
                    <span className="in-app-comms-modal__title">{modal.title}</span>
                    <button
                        className="in-app-comms-modal__close"
                        onClick={handleDismiss}
                        disabled={!canDismiss}
                        aria-label="Dismiss"
                    >
                        ✕
                    </button>
                </div>

                <div className={contentClass}>
                    {modal.media && !mediaError && (
                        <div className="in-app-comms-modal__media">
                            <img
                                src={modal.media.href}
                                alt={modal.media.label}
                                onLoad={handleMediaLoad}
                                onError={() => setMediaError(true)}
                            />
                        </div>
                    )}
                    <div
                        className="in-app-comms-modal__body"
                        dangerouslySetInnerHTML={{ __html: sanitizedBody }}
                    />
                </div>

                {modal.requiresAck && (
                    <div className="in-app-comms-modal__footer">
                        <label>
                            <input
                                type="checkbox"
                                checked={ackChecked}
                                onChange={e => setAckChecked(e.target.checked)}
                            />
                            I acknowledge that I have read this material
                        </label>
                    </div>
                )}

            </div>
        </div>
    )
}
